import { DateTime } from 'luxon';
import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';

import {
  afterDateTime,
  beforeDateTime,
  validDateTime,
} from '@rygen/rygen-ui/composables';
import { renderDateTime } from '@rygen/rygen-ui/i18n';

import i18n from '@/i18n';
import { ShipmentStop, ShipmentStopType, ShippingPoint } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

type KeyOfType<T, V> = keyof {
  [P in keyof T as T[P] extends V ? P : never]: any;
};

export interface Appointment {
  date?: DateTime;
  timeZone?: string;
  field: KeyOfType<ShippingPoint, ShippingPoint['actualArrival']>;
  type: AppointmentType;
  label: string;
  validationRules: Record<string, any>;
  value: string;
}

interface AppointmentRange {
  start: Appointment;
  end: Appointment;
  type: AppointmentType;
  label: string;
  startLabel: string;
  endLabel: string;
}

export enum AppointmentType {
  REQUESTED = 'REQUESTED',
  SCHEDULED = 'SCHEDULED',
  EXPECTED = 'EXPECTED',
  ACTUAL = 'ACTUAL',
}

interface UseShippingAppointmentsResponse {
  appointments: ComputedRef<Appointment[]>;
  primaryAppointment: ComputedRef<Appointment>;
  primaryAppointmentRange: ComputedRef<AppointmentRange | undefined>;
}

const dateFieldPriorityOrder: Readonly<string[]> = Object.freeze([
  'actualDeparture',
  'actualArrival',
  'expectedArrival',
  'scheduledAppointmentEarliest',
  'scheduledAppointmentLatest',
  'requestedAppointmentEarliest',
  'requestedAppointmentLatest',
]);

const dateFieldDisplayOrder: Readonly<string[]> = Object.freeze([
  'requestedAppointmentEarliest',
  'requestedAppointmentLatest',
  'scheduledAppointmentEarliest',
  'scheduledAppointmentLatest',
  'expectedArrival',
  'actualArrival',
  'actualDeparture',
]);

const dateLabelsByFieldAndStopType: Record<
  string,
  Record<ShipmentStopType | string, string>
> = {
  actualArrival: {
    unknown: i18n.global.t('shipments.dates.actualArrival'),
  },
  actualDeparture: {
    unknown: i18n.global.t('shipments.dates.actualDeparture'),
  },
  expectedArrival: {
    [ShipmentStopType.PICKUP]: i18n.global.t('shipments.dates.expectedPickup'),
    [ShipmentStopType.DROP_OFF]: i18n.global.t(
      'shipments.dates.expectedDropoff',
    ),
    unknown: i18n.global.t('shipments.dates.expectedArrival'),
  },
  scheduledAppointmentEarliest: {
    [ShipmentStopType.PICKUP]: i18n.global.t(
      'shipments.dates.scheduledEarlyPickup',
    ),
    [ShipmentStopType.DROP_OFF]: i18n.global.t(
      'shipments.dates.scheduledEarlyDropoff',
    ),
    unknown: i18n.global.t('shipments.dates.scheduledEarlyAppointment'),
  },
  scheduledAppointmentLatest: {
    [ShipmentStopType.PICKUP]: i18n.global.t(
      'shipments.dates.scheduledLatePickup',
    ),
    [ShipmentStopType.DROP_OFF]: i18n.global.t(
      'shipments.dates.scheduledLateDropoff',
    ),
    unknown: i18n.global.t('shipments.dates.scheduledLateAppointment'),
  },
  requestedAppointmentEarliest: {
    [ShipmentStopType.PICKUP]: i18n.global.t('orders.requestEarlyPickup'),
    [ShipmentStopType.DROP_OFF]: i18n.global.t('orders.requestEarlyDropoff'),
    unknown: i18n.global.t('orders.requestEarlyAppointment'),
  },
  requestedAppointmentLatest: {
    [ShipmentStopType.PICKUP]: i18n.global.t('orders.requestLatePickup'),
    [ShipmentStopType.DROP_OFF]: i18n.global.t('orders.requestLateDropoff'),
    unknown: i18n.global.t('orders.requestLateAppointment'),
  },
};

function determineDateLabel(
  fieldName: string,
  stopType?: ShipmentStopType,
): string {
  const byField = dateLabelsByFieldAndStopType[fieldName];
  const byStopType = byField?.[stopType || 'unknown'];
  return byStopType ?? byField.unknown ?? fieldName;
}

export const useShippingAppointments = (
  shippingPoint: Ref<ShippingPoint>,
): UseShippingAppointmentsResponse => {
  const { parentEntity } = useParentEntityStore();

  const appointments = computed(() => {
    let appts: Appointment[] = [];

    const requestedAppointmentEarliestLabel = determineDateLabel(
      'requestedAppointmentEarliest',
      shippingPoint.value.stopType,
    );
    const requestedAppointmentLatestLabel = determineDateLabel(
      'requestedAppointmentLatest',
      shippingPoint.value.stopType,
    );

    const validationRules: Record<string, any> = {
      before: beforeDateTime(
        () => shippingPoint.value.requestedAppointmentLatest,
        requestedAppointmentEarliestLabel,
        requestedAppointmentLatestLabel,
        { strict: false },
      ),
      validDate: validDateTime(requestedAppointmentEarliestLabel),
    };
    if (shippingPoint.value.stopType !== ShipmentStopType.PICKUP) {
      validationRules.afterEarliestPickup = afterDateTime(
        () => parentEntity.value?.origin?.requestedAppointmentEarliest,
        '??',
        '??',
        { strict: false },
      );
    }

    const requestedAppointmentEarliest: Appointment = {
      type: AppointmentType.REQUESTED,
      label: requestedAppointmentEarliestLabel,
      field: 'requestedAppointmentEarliest',
      timeZone: shippingPoint.value.location?.timeZone,
      date: shippingPoint.value.requestedAppointmentEarliest,
      validationRules,
      value: renderDateTime(shippingPoint.value.requestedAppointmentEarliest, {
        separator: '@',
      }),
    };
    appts.push(requestedAppointmentEarliest);

    const latestRequestedAppointment: Appointment = {
      type: AppointmentType.REQUESTED,
      label: requestedAppointmentLatestLabel,
      field: 'requestedAppointmentLatest',
      date: shippingPoint.value.requestedAppointmentLatest,
      timeZone: shippingPoint.value.location?.timeZone,
      validationRules: {
        after: afterDateTime(
          () => shippingPoint.value.requestedAppointmentEarliest,
          requestedAppointmentLatestLabel,
          requestedAppointmentEarliestLabel,
          { strict: false },
        ),
        validDate: validDateTime(requestedAppointmentLatestLabel),
      },
      value: renderDateTime(shippingPoint.value.requestedAppointmentLatest, {
        separator: '@',
      }),
    };
    appts.push(latestRequestedAppointment);

    if (ShipmentStop.isShipmentStop(shippingPoint.value)) {
      const scheduledAppointmentEarliestLabel = determineDateLabel(
        'scheduledAppointmentEarliest',
        shippingPoint.value.stopType,
      );
      const scheduledAppointmentLatestLabel = determineDateLabel(
        'scheduledAppointmentLatest',
        shippingPoint.value.stopType,
      );
      const expectedArrivalLabel = determineDateLabel(
        'expectedArrival',
        shippingPoint.value.stopType,
      );
      const actualArrivalLabel = determineDateLabel(
        'actualArrival',
        shippingPoint.value.stopType,
      );
      const actualDepartureLabel = determineDateLabel(
        'actualDeparture',
        shippingPoint.value.stopType,
      );

      const scheduledAppointmentEarliest: Appointment = {
        type: AppointmentType.SCHEDULED,
        label: scheduledAppointmentEarliestLabel,
        field: 'scheduledAppointmentEarliest',
        date: shippingPoint.value.scheduledAppointmentEarliest,
        timeZone: shippingPoint.value.location?.timeZone,
        validationRules: {
          before: beforeDateTime(
            () =>
              (shippingPoint.value as ShipmentStop).scheduledAppointmentLatest,
            scheduledAppointmentEarliestLabel,
            scheduledAppointmentLatestLabel,
            { strict: false },
          ),
          validDate: validDateTime(scheduledAppointmentEarliestLabel),
        },
        value: renderDateTime(
          shippingPoint.value.scheduledAppointmentEarliest,
          { separator: '@' },
        ),
      };
      appts.push(scheduledAppointmentEarliest);

      const scheduledAppointmentLatest: Appointment = {
        type: AppointmentType.SCHEDULED,
        label: scheduledAppointmentLatestLabel,
        field: 'scheduledAppointmentLatest',
        date: shippingPoint.value.scheduledAppointmentLatest,
        timeZone: shippingPoint.value.location?.timeZone,
        validationRules: {
          after: afterDateTime(
            () =>
              (shippingPoint.value as ShipmentStop)
                .scheduledAppointmentEarliest,
            scheduledAppointmentLatestLabel,
            scheduledAppointmentEarliestLabel,
            { strict: false },
          ),
          validDate: validDateTime(scheduledAppointmentLatestLabel),
        },
        value: renderDateTime(shippingPoint.value.scheduledAppointmentLatest, {
          separator: '@',
        }),
      };
      appts.push(scheduledAppointmentLatest);

      const expectedArrival: Appointment = {
        type: AppointmentType.EXPECTED,
        label: expectedArrivalLabel,
        field: 'expectedArrival',
        date: shippingPoint.value.expectedArrival,
        timeZone: shippingPoint.value.location?.timeZone,
        validationRules: {
          validDate: validDateTime(expectedArrivalLabel),
        },
        value: renderDateTime(shippingPoint.value.expectedArrival, {
          separator: '@',
        }),
      };
      appts.push(expectedArrival);

      const actualArrival: Appointment = {
        type: AppointmentType.ACTUAL,
        label: actualArrivalLabel,
        field: 'actualArrival',
        date: shippingPoint.value.actualArrival,
        timeZone: shippingPoint.value.location?.timeZone,
        validationRules: {
          before: beforeDateTime(
            () => (shippingPoint.value as ShipmentStop).actualDeparture,
            actualArrivalLabel,
            actualDepartureLabel,
            { strict: false },
          ),
          validDate: validDateTime(actualArrivalLabel),
        },
        value: renderDateTime(shippingPoint.value.actualArrival, {
          separator: '@',
        }),
      };
      appts.push(actualArrival);

      const actualDeparture: Appointment = {
        type: AppointmentType.ACTUAL,
        label: actualDepartureLabel,
        field: 'actualDeparture',
        date: shippingPoint.value.actualDeparture,
        timeZone: shippingPoint.value.location?.timeZone,
        validationRules: {
          after: afterDateTime(
            () => (shippingPoint.value as ShipmentStop).actualArrival,
            actualDepartureLabel,
            actualArrivalLabel,
            { strict: false },
          ),
          validDate: validDateTime(actualDepartureLabel),
        },
        value: renderDateTime(shippingPoint.value.actualDeparture, {
          separator: '@',
        }),
      };
      appts.push(actualDeparture);
    }

    appts = appts.sort(
      (a, b) =>
        dateFieldDisplayOrder.indexOf(a.field) -
        dateFieldDisplayOrder.indexOf(b.field),
    );

    return appts;
  });

  const primaryAppointment = computed(() => {
    const appointmentsOrderedByPriority = [...appointments.value].sort(
      (a, b) =>
        dateFieldPriorityOrder.indexOf(a.field) -
        dateFieldPriorityOrder.indexOf(b.field),
    );
    const firstAppointmentWithDate = appointmentsOrderedByPriority.find(
      (appt) => !!appt.date,
    );
    return (
      firstAppointmentWithDate ??
      appointments.value[appointments.value.length - 1]
    );
  });

  const primaryAppointmentRange = computed(() => {
    switch (primaryAppointment.value.field) {
      case 'requestedAppointmentEarliest':
      case 'requestedAppointmentLatest':
        return {
          start: appointments.value[0],
          end: appointments.value[1],
          type: AppointmentType.REQUESTED,
          label:
            shippingPoint.value.stopType === ShipmentStopType.PICKUP
              ? i18n.global.t('shipments.dates.requestedPickup')
              : i18n.global.t('shipments.dates.requestedDropoff'),
          startLabel: i18n.global.t('shipments.dates.earliest'),
          endLabel: i18n.global.t('shipments.dates.latest'),
        };
      case 'scheduledAppointmentEarliest':
      case 'scheduledAppointmentLatest':
        return {
          start: appointments.value[2],
          end: appointments.value[3],
          type: AppointmentType.SCHEDULED,
          label:
            shippingPoint.value.stopType === ShipmentStopType.PICKUP
              ? i18n.global.t('shipments.dates.scheduledPickup')
              : i18n.global.t('shipments.dates.scheduledDropoff'),
          startLabel: i18n.global.t('shipments.dates.earliest'),
          endLabel: i18n.global.t('shipments.dates.latest'),
        };
      case 'actualArrival':
      case 'actualDeparture':
        return {
          start: appointments.value[5],
          end: appointments.value[6],
          type: AppointmentType.ACTUAL,
          label:
            shippingPoint.value.stopType === ShipmentStopType.PICKUP
              ? i18n.global.t('shipments.dates.actualPickup')
              : i18n.global.t('shipments.dates.actualDropoff'),
          startLabel: i18n.global.t('shipments.dates.arrival'),
          endLabel: i18n.global.t('shipments.dates.departure'),
        };
      default:
        return undefined;
    }
  });

  return {
    appointments,
    primaryAppointment,
    primaryAppointmentRange,
  };
};
